import { useSuspenseQuery } from "@tanstack/react-query"
import { useCallback, useMemo } from "react"

/**
 * Fetches the organization's grade list from the People API.
 * Returns grades with structure: { id, attributes: { key, value } }
 */
export function useOrganizationGrades() {
  const { data } = useSuspenseQuery({
    queryKey: ["/people/v2/grades"],
    refetchOnWindowFocus: false,
    retry: false,
    gcTime: Infinity,
    staleTime: Infinity,
  })

  return data
}

/**
 * Returns grade options formatted for Select components.
 * @returns Array of { label, value } objects for use in dropdowns
 */
export function useOrganizationGradeOptions() {
  const grades = useOrganizationGrades()

  return useMemo(() => {
    if (!grades?.data) return []

    return grades.data.map((grade) => ({
      label: grade.attributes.value,
      value: grade.attributes.key,
    }))
  }, [grades])
}

/**
 * Returns a function to convert grade keys to their display labels.
 * @returns {Function} Function that takes a grade key and returns its label
 */
export function useOrganizationGradeLabel() {
  const grades = useOrganizationGrades()

  return useCallback(
    (gradeKey) => {
      return gradeKeyToLabel(gradeKey, grades?.data)
    },
    [grades],
  )
}

/**
 * Converts a grade key to its display label.
 * @param {number} gradeKey - The grade key to convert
 * @param {Array} grades - Array of grade objects from the API
 * @returns {string} The display label for the grade, or empty string if not found
 */
export function gradeKeyToLabel(gradeKey, grades) {
  if (gradeKey === null || gradeKey === undefined) return ""
  if (!grades || !Array.isArray(grades)) return ""

  const grade = grades.find((g) => g.attributes.key === gradeKey)
  return grade ? grade.attributes.value : ""
}
